import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

import { type DynamicWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { type EVENTS } from '@apps/web/lib/events';

export type DynamicWidgetMetricEvent =
    | typeof EVENTS.DYNAMIC_WIDGET_BUILT
    | typeof EVENTS.DYNAMIC_WIDGET_PRESENTATION
    | typeof EVENTS.DYNAMIC_WIDGET_FIRST_USEFUL
    | typeof EVENTS.DYNAMIC_WIDGET_FALLBACK
    | typeof EVENTS.DYNAMIC_WIDGET_BINDING_MISSING
    | typeof EVENTS.DYNAMIC_WIDGET_ACTION_SUBMITTED
    | typeof EVENTS.DYNAMIC_WIDGET_ACTION_SUCCEEDED
    | typeof EVENTS.DYNAMIC_WIDGET_ACTION_FAILED
    | typeof EVENTS.DYNAMIC_WIDGET_ACTION_TERMINAL_TIMING
    | typeof EVENTS.DYNAMIC_WIDGET_ISSUE_SHOWN
    | typeof EVENTS.DYNAMIC_WIDGET_DIAGNOSTIC_ATTACHED;

export type DynamicWidgetMetricTags = {
    readonly template: DynamicWidgetModel['template'];
    readonly templateVersion: number;
    readonly toolName: string;
    readonly errorCode?: string;
    readonly errorClass?: string;
    readonly outcome?:
        | 'candidate'
        | 'rendered'
        | 'replaced_same_result'
        | 'supporting_result'
        | 'malformed_fallback'
        | 'classic_fallback';
    readonly durationMs?: number;
};

export type PostHogCaptureClient = {
    readonly capture: (
        event: string,
        properties: Record<string, string | number>,
    ) => void;
};

export function getDynamicWidgetMetricTags(
    model: DynamicWidgetModel,
): DynamicWidgetMetricTags {
    const eventSource = model.sources.find(
        (source) => source.kind === 'event_source',
    );
    return {
        template: model.template,
        templateVersion: model.templateVersion,
        toolName: eventSource?.toolName ?? 'unknown',
    };
}

/**
 * Keeps the analytics boundary closed: call sites can supply only approved
 * low-cardinality labels plus one bounded duration. Undefined tags are omitted.
 */
export function captureDynamicWidgetMetric(
    posthog: PostHogCaptureClient | null | undefined,
    event: DynamicWidgetMetricEvent,
    tags: DynamicWidgetMetricTags,
): void {
    const properties: Record<string, string | number> = {
        template: tags.template,
        templateVersion: tags.templateVersion,
        toolName: tags.toolName,
    };
    if (tags.errorCode !== undefined) {
        properties['errorCode'] = tags.errorCode;
    }
    if (tags.errorClass !== undefined) {
        properties['errorClass'] = tags.errorClass;
    }
    if (tags.outcome !== undefined) {
        properties['outcome'] = tags.outcome;
    }
    if (tags.durationMs !== undefined && Number.isFinite(tags.durationMs)) {
        properties['durationMs'] = Math.min(
            24 * 60 * 60 * 1000,
            Math.max(0, Math.round(tags.durationMs)),
        );
    }
    posthog?.capture(event, properties);
}

export function DynamicWidgetMetricReporter({
    event,
    tags,
    dedupeKey,
}: {
    readonly event: DynamicWidgetMetricEvent;
    readonly tags: DynamicWidgetMetricTags;
    readonly dedupeKey: string;
}) {
    const posthog = usePostHog();
    const reportedKeys = useRef<Set<string>>(new Set());

    useEffect(() => {
        const key = `${event}:${dedupeKey}`;
        if (reportedKeys.current.has(key)) {
            return;
        }
        reportedKeys.current.add(key);
        captureDynamicWidgetMetric(posthog, event, tags);
    }, [dedupeKey, event, posthog, tags]);

    return null;
}
